
/* ==========================================================================
 AS_US_QV — Quick View (production build)
 --------------------------------------------------------------------------
 - Buttons are rendered server-side via snippets/as-us-qv-button.liquid
   (one per product card). This script does NOT scan/inject the DOM for
   buttons, so it costs nothing extra whether the grid has 20 or 200 cards.
 - Modal is a single global element (snippets/as-us-qv-modal.liquid),
   rendered once per page and reused for every product clicked.
 - All selectors are namespaced with as_us_qv- / data-as-us-qv-* so it
   cannot collide with existing theme JS/CSS.

 ADD TO CART: this theme's own <product-form-component> already handles
 add-to-cart submissions itself — it's bound as a document-level CAPTURE
 listener in the theme's core component.js, and its handleSubmit() does
 the AJAX POST to /cart/add.js, refreshes the cart-drawer section, AND
 opens the cart drawer, all natively. We do NOT re-implement any of that
 here (an earlier version of this file did, which caused every add-to-cart
 from Quick View to fire TWICE — once from the theme's own handler and
 once from ours — doubling the quantity and cart count). All we do now is
 close the Quick View modal shortly after a submit, so the person sees
 their own cart drawer (opened by the theme) instead of the modal.
 ========================================================================== */
 (function () {
  "use strict";

  // Guard against this script being loaded twice on the same page
  // (e.g. included in both theme.liquid and an app embed) — a second
  // run would double-bind every delegated listener below.
  if (window.__asUsQvInitialized) return;
  window.__asUsQvInitialized = true;

  function waitUntil(predicate, success, error) {
    var int = setInterval(function () {
      if (predicate()) {
        clearInterval(int);
        int = null;
        success();
      }
    }, 33);

    setTimeout(function () {
      if (int !== null) {
        clearInterval(int);
        if (typeof error === "function") error();
      }
    }, 10000);
  }

  // Generation token: guards against a stale (older) load hiding the loader
  // — or, more importantly, re-initializing Slick on markup that belongs to
  // a NEWER render — when the user clicks buttons/swatches rapidly.
  window.__asUsQvGen = 0;

  /* ------------------------------------------------------------------
     In-session caches (per page load).
     - productCache: /products/handle.js JSON → used for the gallery
     - htmlCache:    product page HTML        → used for the details panel
     Both cache the in-flight PROMISE so hover-prefetch + click reuse the
     same request, and re-opening the same product/color is instant.
  ------------------------------------------------------------------ */
  var productCache = {};
  var htmlCache = {};

  function getProduct(handle) {
    if (!handle) return Promise.reject(new Error("as_us_qv: missing handle"));
    if (!productCache[handle]) {
      productCache[handle] = fetch("/products/" + handle + ".js")
        .then(function (r) { return r.json(); })
        .catch(function (e) { delete productCache[handle]; throw e; });
    }
    return productCache[handle];
  }

  function getPageHtml(url) {
    if (!url) return Promise.reject(new Error("as_us_qv: missing url"));
    if (!htmlCache[url]) {
      htmlCache[url] = fetch(url)
        .then(function (r) { return r.text(); })
        .catch(function (e) { delete htmlCache[url]; throw e; });
    }
    return htmlCache[url];
  }

  /* ------------------------------------------------------------------
     Destroy slick sliders BEFORE dropping their markup, so no orphaned
     slick state (or its window resize handlers) is left behind on a
     color/product change.

     Scoped to one gallery wrapper, because on a color switch the new
     gallery is built on top of the old one and the two coexist for a
     moment — an unscoped `$(".as_us_qv-slider-for")` would tear down
     (or re-init) both at once.
  ------------------------------------------------------------------ */
  function destroySlidersIn(root) {
    if (!root) return;
    var $for = $(root).find(".as_us_qv-slider-for");
    var $nav = $(root).find(".as_us_qv-slider-nav");
    if ($for.hasClass("slick-initialized")) $for.slick("unslick");
    if ($nav.hasClass("slick-initialized")) $nav.slick("unslick");
  }

  function galleryColumn() {
    return document.querySelector("#as_us_qv-content .as_us_qv-left-side-content");
  }

  function destroySliders() {
    destroySlidersIn(galleryColumn());
  }

  /* Remove any gallery that was staged for a switch that never committed
     (superseded by a newer swatch click, or an aborted render). */
  function discardStagedGalleries() {
    var column = galleryColumn();
    if (!column) return;
    [].slice.call(column.querySelectorAll(".as_us_qv-gallery-staging")).forEach(function (el) {
      destroySlidersIn(el);
      if (el.parentNode) el.parentNode.removeChild(el);
    });
  }

  /* Fetch and decode the incoming images BEFORE they are swapped in, so
     the gallery is never shown half-painted. Bounded by PRELOAD_TIMEOUT:
     a slow or broken image can delay the swap, never block it. Only the
     images the gallery actually shows at rest (main + thumbnail rail)
     are waited on. */
  var PRELOAD_TIMEOUT = 1500;

  function preloadImages(urls) {
    var loads = (urls || []).slice(0, 6).map(function (src) {
      return new Promise(function (resolve) {
        var img = new Image();
        img.onload = function () {
          if (typeof img.decode === "function") img.decode().then(resolve, resolve);
          else resolve();
        };
        img.onerror = function () { resolve(); };
        img.src = src;
      });
    });
    return Promise.race([
      Promise.all(loads),
      new Promise(function (resolve) { setTimeout(resolve, PRELOAD_TIMEOUT); })
    ]);
  }

  /* renderGallery fires onReady ONLY after slick has initialized, so the
     caller can keep the loader up until the gallery is actually laid out
     (avoids flicker/reflow).

     opts.staged = true (color-switch path): the gallery is built OVER the
     one currently on screen instead of replacing it, and onReady is handed
     a commit() — call it to reveal this gallery and drop the previous one
     in a single frame. That lets the caller line the gallery swap up with
     the details swap, so the two columns are never out of step.

     `gen` is the generation token captured by the CALLER at the moment it
     decided to render. We re-check it again inside the setTimeout below,
     because 30ms is enough time for a newer click to swap the DOM out from
     under us — without this check, a stale render would re-run Slick init
     on markup that already belongs to a different product/color, which is
     what caused the gallery to end up blank and the modal to visibly
     "blink" when swatches were clicked quickly. */
  var galleryUid = 0;

  function renderGallery(product, onReady, gen, opts) {
    opts = opts || {};
    var column = galleryColumn();
    if (!column) return;

    var images = (product.images || [])
      .map(function (img, index) {
        return (
          '<div class="as_us_qv-thumb-slide"><img src="' + img + '" ' +
          'class="as_us_qv-thumb' + (index === 0 ? " active" : "") + '" ' +
          'data-image="' + img + '" /></div>'
        );
      })
      .join("");

    var imagesNav = (product.images || [])
      .map(function (img, index) {
        return (
          '<div><img src="' + img + '" ' +
          'class="as_us_qv-thumb' + (index === 0 ? " active" : "") + '" ' +
          'data-image="' + img + '" /></div>'
        );
      })
      .join("");

    // Every gallery gets its own id so Slick's `asNavFor` (a selector
    // string) can only ever reach its OWN nav, even while a staged
    // gallery and the one still on screen share the column.
    var uid = "as_us_qv-gallery-" + ++galleryUid;
    var holder = document.createElement("div");
    holder.innerHTML =
      '<div class="as_us_qv-wrapper" id="' + uid + '">' +
        '<div class="as_us_qv-gallery">' +
          '<div class="as_us_qv-img-slide">' +
            '<div class="as_us_qv-slider-for">' + images + "</div>" +
            '<div class="as_us_qv-slider-nav">' + imagesNav + "</div>" +
          "</div>" +
        "</div>" +
      "</div>";
    var wrapper = holder.firstChild;

    if (opts.staged) {
      // Color switch: lay the new gallery over the current one, invisible.
      // It is laid out for real (see .as_us_qv-gallery-staging in the CSS),
      // which is what Slick needs to measure itself, while the shopper
      // keeps seeing the images already on screen until commit().
      discardStagedGalleries();
      wrapper.className += " as_us_qv-gallery-staging";
      column.appendChild(wrapper);
    } else {
      destroySliders();
      column.innerHTML = "";
      column.appendChild(wrapper);
    }

    function discard() {
      destroySlidersIn(wrapper);
      if (wrapper.parentNode) wrapper.parentNode.removeChild(wrapper);
    }

    /* Show this gallery and drop the one it replaces, in a single frame,
       so the column never goes empty between the two. */
    function commit() {
      if (!opts.staged) return;
      [].slice.call(column.querySelectorAll(".as_us_qv-wrapper")).forEach(function (old) {
        if (old === wrapper) return;
        destroySlidersIn(old);
        if (old.parentNode) old.parentNode.removeChild(old);
      });
      wrapper.classList.remove("as_us_qv-gallery-staging");
      // Slick measured itself while the wrapper was positioned over the
      // old gallery; let it re-measure now that it is back in flow.
      var $for = $(wrapper).find(".as_us_qv-slider-for");
      var $nav = $(wrapper).find(".as_us_qv-slider-nav");
      if ($for.hasClass("slick-initialized")) $for.slick("setPosition");
      if ($nav.hasClass("slick-initialized")) $nav.slick("setPosition");
    }

    setTimeout(function () {
      // A newer click may have started a different render during this
      // 30ms window — bail out so we never re-init Slick on markup that
      // no longer belongs to this generation.
      if (typeof gen !== "undefined" && gen !== window.__asUsQvGen) {
        discard();
        return;
      }

      // Bottom-align the thumbnail rail WITHOUT breaking Slick's own
      // windowing: use the actual image count (capped at 5) as
      // slidesToShow, so Slick itself divides the available height
      // evenly across however many thumbnails really exist. Forcing
      // slidesToShow:5 when a product only has e.g. 4 images used to
      // leave a gap — but overriding Slick's track/slide CSS heights to
      // "fix" that gap (an earlier attempt) broke Slick's internal
      // windowing entirely, causing every thumbnail to stack and the
      // whole modal to balloon in height on every color switch. This is
      // the safe way to get the same visual result.
      var navSlideCount = Math.max(1, Math.min(5, (product.images || []).length));

      $("#" + uid + " .as_us_qv-slider-for").slick({
        slidesToShow: 1,
        slidesToScroll: 1,
        arrows: false,
        fade: true,
        asNavFor: "#" + uid + " .as_us_qv-slider-nav"
      });

      $("#" + uid + " .as_us_qv-slider-nav").slick({
        slidesToShow: navSlideCount,
        slidesToScroll: 1,
        asNavFor: "#" + uid + " .as_us_qv-slider-for",
        dots: false,
        focusOnSelect: true,
        vertical: true,
        verticalSwiping: true,
        responsive: [
          {
            breakpoint: 768,
            settings: { vertical: false, verticalSwiping: false, slidesToShow: navSlideCount }
          }
        ]
      });

      // Inject dynamic "recently bought" stock line into this gallery's
      // slider. Scoped to this wrapper \u2014 a staged gallery has to get its
      // own line even though the gallery it replaces still carries one.
      var sliderList = $("#" + uid + " .as_us_qv-img-slide .as_us_qv-slider-for.slick-slider .slick-list");
      if (sliderList.length && !$("#" + uid + " .as_us_qv-stock-section").length) {
        var stockBlock = document.createElement("div");
        stockBlock.className = "as_us_qv-stock-section";
        stockBlock.innerHTML =
          '<div class="as_us_qv-stock-bottom">' +
            '<span class="as_us_qv-stock-count">\uD83E\uDD1D</span>' +
            '<span class="as_us_qv-demand-text"></span>' +
          "</div>";
        sliderList[0].appendChild(stockBlock);
        updateDemandText(wrapper);
      }

      if (typeof onReady === "function") onReady(commit);
    }, 30);
  }

  /* ------------------------------------------------------------------
     Inject the product-details fragment from the fetched page HTML.
     Scripts are stripped so nothing double-executes; theme custom
     elements (variant-picker, etc.) still upgrade normally on insert.

     preserveSwatches = true (color-switch path): the EXISTING swatch
     container node is kept and reused inside the new fragment before
     injecting, instead of taking the new fragment's own swatch markup.
     This is what stops the swatch row from re-ordering itself on every
     click — the order is fixed the moment the modal first opens.
  ------------------------------------------------------------------ */
  /* ------------------------------------------------------------------
     Figma shows the sold count ("516,882 pairs sold") above the star
     rating. Two things stood in the way, both handled here on the
     detached fragment before it is injected, so nothing visibly jumps:

     1. It was hidden. as-us-qv.css hides every custom-styled text block
        pulled in from the PDP (shipping, fit, bundle promos), and the
        sold count is styled the same way. Tagging it with
        .as_us_qv-sold-count exempts it from that rule.
     2. It was below the rating. The product templates order the rating
        first, so it is moved above.

     Scoped to the quick view; the product page itself is untouched.

     Found by content, because the block id differs per template
     (text_EJzb9f on Stella, text_yfPdjc on Lucie). The pattern must
     match the block's whole text, so a "Sold out" label, or a product
     description that happens to mention sales, can never match.
  ------------------------------------------------------------------ */
  var SOLD_COUNT_TEXT = /^\s*\d[\d,]*\s+\w+\s+sold\s*$/i;

  function placeSoldCountAboveRating(root) {
    var soldCount = null;
    root.querySelectorAll(".text-block").forEach(function (el) {
      if (!soldCount && SOLD_COUNT_TEXT.test(el.textContent)) soldCount = el;
    });
    if (!soldCount) return;

    soldCount.classList.add("as_us_qv-sold-count");

    // Only reorder among siblings — never lift the block into a
    // different container than the one the template put it in.
    var rating = root.querySelector(".rating-wrapper");
    if (rating && rating.parentNode === soldCount.parentNode) {
      rating.parentNode.insertBefore(soldCount, rating);
    }
  }

  /* ------------------------------------------------------------------
     App blocks that build their own contents with their own JS cannot
     work in here: the scripts are stripped just above (so the theme's
     own scripts never double-execute), which leaves those blocks as an
     empty grey placeholder that never fills.

     Sternify's "Frequently Bought Together" upsell is one of them. It is
     added to the product template from the Shopify theme editor and
     renders correctly on the product page itself; only the copy pulled
     into this drawer is inert. A bundle upsell does not belong in a
     quick shop drawer anyway — its own add-to-cart is not wired to the
     interception below — so it is dropped rather than left showing.
  ------------------------------------------------------------------ */
  function removeScriptDrivenAppBlocks(root) {
    root.querySelectorAll('[id^="shopify-block-"]').forEach(function (block) {
      if (/sternify/i.test(block.id)) block.remove();
    });
  }

  function renderDetails(html, handle, done, preserveSwatches) {
    var doc = new DOMParser().parseFromString(html, "text/html");
    var frag = doc.querySelector(".product-details.sticky-content--desktop");
    var $right = $("#as_us_qv-content .as_us_qv-right-side-content");

    if (frag) {
      frag.querySelectorAll("script").forEach(function (s) { s.remove(); });
      removeScriptDrivenAppBlocks(frag);
      placeSoldCountAboveRating(frag);

      if (preserveSwatches) {
        var oldSwatches = document.querySelector("#as_us_qv-content .product-swatch-container");
        var newSwatches = frag.querySelector(".product-swatch-container");
        if (oldSwatches && newSwatches) {
          // The "Color: <name>" legend is rendered INSIDE the swatch
          // container (blocks/swatches.liquid), so reusing that container
          // to keep the swatch order also froze the colour name with it —
          // a Smooth Doe product kept reading "Color: Black". Carry the
          // incoming page's value over before the node is reused.
          var oldValue = oldSwatches.querySelector(".variant-option__swatch-value");
          var newValue = newSwatches.querySelector(".variant-option__swatch-value");
          if (oldValue && newValue) oldValue.innerHTML = newValue.innerHTML;

          newSwatches.replaceWith(oldSwatches); // reuse old node -> keeps its order
        }
      }

      $right.html(frag.outerHTML);

      // Mark the swatch matching the current handle as active — the
      // theme's own "selected" state was tied to the OLD swatch node's
      // origin page, so we own the active indicator ourselves from here.
      //
      // IMPORTANT: the reused swatch container (preserveSwatches path)
      // can carry a stale native `checked` state on its underlying
      // radio/checkbox input from whenever it was first captured — that
      // native checked state drives the theme's own CSS selection ring
      // independently of our `.as_us_qv-swatch--active` class, which is
      // what made the DEFAULT swatch look "still selected" even after
      // clicking a different one. So we now explicitly sync the input's
      // checked PROPERTY (not just our class) to match the current handle.
      $right.find(".product-swatch").each(function () {
        var h = this.href ? this.href.split("/products/")[1].split("?")[0] : null;

        // Swatches with no real href represent the ORIGINAL product the
        // modal was first opened with (frozen into the reused container,
        // since on its own page "itself" never links out) — resolve it.
        if (!h && window.__asUsQvInitialHandle) {
          h = window.__asUsQvInitialHandle;
        }

        var isActive = !!h && h === handle;

        $(this)
          .toggleClass("as_us_qv-swatch--active", isActive)
          .removeAttr("aria-current")
          // Strip any native "currently selected" indicator classes the
          // theme may have baked into this swatch's own markup — since the
          // container is frozen/reused, these never clear on their own and
          // end up showing alongside our own active indicator (two swatches
          // looking "selected" at once).
          .toggleClass("active", isActive)
          .toggleClass("selected", isActive)
          .toggleClass("is-selected", isActive)
          .toggleClass("current", isActive)
          .toggleClass("is-active", isActive);

        var $input = $(this).find('input[type="radio"], input[type="checkbox"]');
        if ($input.length) {
          $input.prop("checked", isActive);
        }
      });
      $right.find(".product-swatch.as_us_qv-swatch--active").attr("aria-current", "true");
    } else {
      $right.empty();
    }

    var ratingLink = $("#as_us_qv-content .rating-count a");
    if (ratingLink.length) {
      var currentHref = ratingLink.attr("href");
      if (currentHref && currentHref.charAt(0) === "#") {
        ratingLink.attr("href", "/products/" + handle + currentHref);
      }
    }

    var addToCartText = $("#as_us_qv-content .add-to-cart-text");
    if (addToCartText.length) {
      var allText = addToCartText.text().trim();
      var lines = allText.split("\n").map(function (l) { return l.trim(); }).filter(Boolean);
      if (lines.length > 0) {
        addToCartText.empty().text(lines[0].replace(/•/g, "|"));
      }
    }

    if (typeof done === "function") done();
  }

  /* ------------------------------------------------------------------
     Shared renderer, with two different strategies:

     FIRST OPEN — the modal is empty behind the loader, so there is
     nothing to protect: gallery (product JSON) and details (page HTML)
     load IN PARALLEL and each renders as soon as it arrives. The loader
     lifts only once BOTH are ready, so the gallery never pops/reflows
     after it disappears.

     COLOR SWITCH — the shopper is already looking at a product, so
     nothing is torn down until the replacement is fetched, decoded and
     laid out; gallery and details then swap in the SAME frame. The old
     behaviour emptied the gallery on click and let each side land
     whenever it arrived, which is what made the modal flicker: the
     images went white for ~500ms, everything dimmed to 40%, and the new
     photos showed next to the previous name/price/swatch before the
     right side caught up.
  ------------------------------------------------------------------ */
  function populateModal(productPromise, htmlPromise, handle, opts) {
    opts = opts || {};
    var isSwitch = !!opts.isColorSwitch; // true = swatch click, false = initial open

    var gen = ++window.__asUsQvGen;

    function stale() { return gen !== window.__asUsQvGen; } // a newer click superseded this one

    function clearBusyState() {
      $(".as_us_qv-container").removeClass("as_us_qv-loader");
      $("#as_us_qv-content").removeClass("as_us_qv-switching");
    }

    if (isSwitch) {
      Promise.all([
        productPromise.catch(function () { return null; }),
        htmlPromise.catch(function () { return null; })
      ]).then(function (res) {
        var product = res[0];
        var html = res[1];
        if (stale()) return;

        // No product JSON: the gallery can't be rebuilt, but the details
        // still can — swap those and leave the images alone.
        if (!product) {
          if (html) renderDetails(html, handle, null, true);
          clearBusyState();
          return;
        }

        preloadImages(product.images).then(function () {
          if (stale()) return;
          renderGallery(product, function (commitGallery) {
            if (stale()) return;
            requestAnimationFrame(function () {
              if (stale()) return;
              if (html) renderDetails(html, handle, null, true);
              commitGallery();
              clearBusyState();
            });
          }, gen, { staged: true });
        });
      });
    } else {
      var leftReady = false;
      var rightReady = false;

      var hideBusyState = function () {
        if (stale()) return;
        if (leftReady && rightReady) clearBusyState();
      };

      htmlPromise
        .then(function (html) {
          if (stale()) return;
          renderDetails(html, handle, function () { rightReady = true; hideBusyState(); }, false);
        })
        .catch(function () { rightReady = true; hideBusyState(); });

      productPromise
        .then(function (product) {
          if (stale()) return;
          renderGallery(product, function () { leftReady = true; hideBusyState(); }, gen);
        })
        .catch(function () { leftReady = true; hideBusyState(); });
    }

    // Safety net: never let a busy state get stuck if a request stalls.
    setTimeout(function () {
      if (!stale()) clearBusyState();
    }, 8000);
  }

  function updateDemandText(root) {
    var el = (root || document.querySelector("#as_us_qv-content")).querySelector(".as_us_qv-demand-text");
    if (!el) return;

    var handle = window.__asUsQvCurrentHandle || "product";
    var storageKey = "as_us_qv_demand_" + handle;

    var demand = localStorage.getItem(storageKey);
    if (!demand) {
      demand = 30 + Math.floor(Math.random() * 20);
      localStorage.setItem(storageKey, demand);
    }
    el.textContent = demand + " people bought this product recently.";
  }

  /* First open — from the Quick View button on a product card.
     Full loader is expected/fine here since the modal is empty anyway. */
  function openModalFor(handle, url) {
    $("#as_us_qv-modal").addClass("as_us_qv-active");
    $(".as_us_qv-container").addClass("as_us_qv-loader");

    window.__asUsQvCurrentHandle = handle;
    // Remember the ORIGINAL product this modal was opened with. Its own
    // swatch (once the container gets frozen/reused on the first color
    // switch) will never carry a real href, since on its own page "itself"
    // never links out. We use this to resolve clicks on that swatch.
    window.__asUsQvInitialHandle = handle;
    window.__asUsQvInitialUrl = url;

    destroySliders();
    $("#as_us_qv-content .as_us_qv-right-side-content").empty();
    $("#as_us_qv-content .as_us_qv-left-side-content").empty();

    populateModal(getProduct(handle), getPageHtml(url), handle, { isColorSwitch: false });
  }

  /* Color switch — from a swatch INSIDE an already-open modal.
     No full black loader, swatch row keeps its DOM/order, and the
     product currently on screen stays fully visible and intact until
     its replacement is ready to take over in one go (see populateModal). */
  function switchColor(handle, url) {
    if (handle === window.__asUsQvCurrentHandle) return; // already showing this one

    $("#as_us_qv-content").addClass("as_us_qv-switching");
    // Optimistic active-state so the click feels instant.
    $("#as_us_qv-content .product-swatch").each(function () {
      var h = this.href ? this.href.split("/products/")[1].split("?")[0] : null;
      if (!h && window.__asUsQvInitialHandle) {
        h = window.__asUsQvInitialHandle;
      }
      var isActive = !!h && h === handle;
      $(this)
        .toggleClass("as_us_qv-swatch--active", isActive)
        .toggleClass("active", isActive)
        .toggleClass("selected", isActive)
        .toggleClass("is-selected", isActive)
        .toggleClass("current", isActive)
        .toggleClass("is-active", isActive);
      var $input = $(this).find('input[type="radio"], input[type="checkbox"]');
      if ($input.length) {
        $input.prop("checked", isActive);
      }
    });

    window.__asUsQvCurrentHandle = handle;

    // NOTE: the gallery is deliberately NOT emptied here. It used to be,
    // and that single line is what blanked the images for the whole
    // duration of the fetch. The swap now happens in populateModal once
    // the new gallery is built and decoded.
    populateModal(getProduct(handle), getPageHtml(url), handle, { isColorSwitch: true });
  }

  function closeModal() {
    $("#as_us_qv-modal").removeClass("as_us_qv-active");
    $("#as_us_qv-content .as_us_qv-right-side-content").empty();
    // Unslick BEFORE the markup goes away, otherwise slick's window
    // resize handlers outlive the DOM they were bound for.
    destroySliders();
    $("#as_us_qv-content .as_us_qv-left-side-content").empty();
  }

  /* ------------------------------------------------------------------
     ADD TO CART (from inside the Quick View modal)
     AJAX add → refresh EVERY cart-drawer-component instance's HTML
     (line items/subtotal/count) → open the cart drawer → close the
     quick view modal. No page reload, and no duplicate add.

     Why interception happens on `window` (not `document`): this theme's
     own <product-form-component> has a document-level CAPTURE-phase
     submit listener (bound in its core component.js) that performs its
     OWN add-to-cart POST. Since capture-phase listeners fire in this
     order — window → document → ... → target — a listener we bind on
     `document` always runs AFTER the theme's, too late to stop it. But a
     capture-phase listener on `window` fires BEFORE document's, so THIS
     is where we can genuinely intercept and cancel the event before the
     theme's own handler ever sees it — preventing the double /cart/add.js
     call (and the doubled quantity/count it was causing) for good.
  ------------------------------------------------------------------ */
  function submitAddToCart($form) {
    var formEl = $form.get(0);
    var formData = new FormData(formEl);
    var $submitBtn = $form.find('[type="submit"]');

    if ($submitBtn.prop("disabled")) return;
    $submitBtn.prop("disabled", true);

    fetch("/cart/add.js", {
      method: "POST",
      headers: { "Accept": "application/json" },
      body: formData
    })
      .then(function (res) { return res.json(); })
      .then(function (data) {
        if (data.status) {
          console.error("as_us_qv: add to cart failed", data.description || data.message);
          alert(data.description || "Could not add item to cart.");
          return Promise.reject(data);
        }

        closeModal();

        var $cartItemsComponent = $("cart-items-component").first();
        var sectionId = $cartItemsComponent.attr("data-section-id");
        if (!sectionId) return;

        return fetch("/?sections=" + sectionId)
          .then(function (r) { return r.json(); })
          .then(function (sections) {
            var html = sections[sectionId];
            if (!html) return;

            var doc = new DOMParser().parseFromString(html, "text/html");
            var newDrawer = doc.querySelector("cart-drawer-component");

            // Update EVERY cart-drawer-component instance on the page
            // (some themes render a second copy for a sticky/mobile
            // header) so the bubble count refreshes everywhere.
            if (newDrawer) {
              document.querySelectorAll("cart-drawer-component").forEach(function (oldDrawer) {
                oldDrawer.innerHTML = newDrawer.innerHTML;
              });
            }

            // Swapping innerHTML above wipes out the trigger button's own
            // click-binding, so open the drawer directly via the native
            // <dialog> API instead of relying on that button.
            var dialogEl = document.querySelector("cart-drawer-component dialog");
            if (dialogEl) {
              try {
                if (!dialogEl.open) {
                  if (typeof dialogEl.showModal === "function") {
                    dialogEl.showModal();
                  } else {
                    dialogEl.setAttribute("open", "");
                  }
                }
              } catch (dialogErr) {
                dialogEl.setAttribute("open", "");
              }
            } else {
              var cartDrawer = document.querySelector("cart-drawer-component");
              if (cartDrawer && typeof cartDrawer.open === "function") {
                cartDrawer.open();
              } else {
                var trigger = document.querySelector('[data-testid="cart-drawer-trigger"]');
                if (trigger) trigger.click();
              }
            }
          });
      })
      .catch(function (err) {
        if (err && err.status) return; // already alerted above
        console.error("as_us_qv: add to cart error", err);
      })
      .finally(function () {
        $submitBtn.prop("disabled", false);
      });
  }

  // Intercept on WINDOW, capture phase — fires before the theme's own
  // document-level capture listener, so we can genuinely stop it from
  // also running (see the long comment on submitAddToCart above).
  window.addEventListener(
    "submit",
    function (e) {
      var formEl = e.target && e.target.closest && e.target.closest("#as_us_qv-modal .shopify-product-form");
      if (!formEl) return;

      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();

      submitAddToCart($(formEl));
    },
    true // capture = true
  );

  waitUntil(
    function () { return typeof window.jQuery === "function"; },
    function () {
      // Modal + buttons already exist in the DOM via Liquid — no injection,
      // no MutationObserver needed. Just bind delegated listeners once.

      /* Warm caches on hover so the modal opens instantly on click. */
      $(document).on("mouseenter", ".as_us_qv-btn", function () {
        var handle = $(this).data("as-us-qv-handle");
        var url = $(this).data("as-us-qv-url");
        if (handle) getProduct(handle);
        if (url) getPageHtml(url);
      });

      $(document).on("mouseenter", ".product-swatch", function () {
        if (!this.href || this.href.indexOf("/products/") === -1) return;
        var handle = this.href.split("/products/")[1].split("?")[0];
        getProduct(handle);
        getPageHtml(this.href);
      });

      /* Size selector — updates price / add-to-cart state inside the modal. */
      $(document).on("click", ".variant-option__button-label", function () {
        var $rightSide = $(this).closest(".as_us_qv-right-side-content");
        var dataId = $(this).find("input").attr("data-variant-id");
        var $addToCartText = $rightSide.find(".add-to-cart-text");

        var price = $rightSide.find('div[ref="priceContainer"] .price').first().text().trim();
        if (!price) {
          var match = ($addToCartText.text() || "").match(/\$[\d.,]+/);
          price = match ? match[0] : "";
        }

        $rightSide.find(".shopify-product-form input[name='id']").val(dataId);
        var priceSuffix = price ? " | " + price : "";

        if ($(this).find("input").attr("data-option-available") === "true") {
          $addToCartText.empty().text("Add to cart" + priceSuffix);
          $rightSide.find(".add-to-cart-button:not(.button-secondary)").removeAttr("disabled");
        } else {
          $addToCartText.empty().text("Sold out" + priceSuffix);
          $rightSide.find(".add-to-cart-button:not(.button-secondary)").attr("disabled", true);
        }
      });

      /* Open modal from a product card's Quick View button. */
      $(document).on("click", ".as_us_qv-btn", function (e) {
        e.preventDefault();
        var handle = $(this).data("as-us-qv-handle");
        var url = $(this).data("as-us-qv-url");
        openModalFor(handle, url);
      });

      /* Close modal. */
      $(document).on("click", ".as_us_qv-close, .as_us_qv-overlay", function () {
        closeModal();
      });

      /* Change color variant inside the modal (swatch → different product). */
      $(document).on("click", "#as_us_qv-modal .product-swatch", function (e) {
        e.preventDefault();
        e.stopPropagation();
        var url = this.href;
        var handle;

        if (!url || url.indexOf("/products/") === -1) {
          // This swatch has no real href — it's the "self" swatch frozen
          // into the reused container from the product the modal was
          // FIRST opened with. Resolve it back to that original product.
          handle = window.__asUsQvInitialHandle;
          url = window.__asUsQvInitialUrl;
        } else {
          handle = url.split("/products/")[1].split("?")[0];
        }

        if (!handle || !url) return;

        switchColor(handle, url);
        return false;
      });

      /* Add to cart is handled by the window-capture listener defined
         above (submitAddToCart) — nothing additional needed here. */
    }
  );
})();